import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, FormHelperText, Grid, Typography } from "@mui/material";
import React from "react";
import { DateField } from "../../../components/FormComponents/DateField";
import { FileUploadField } from "../../../components/FormComponents/FileUploadField";
import { NumericInput } from "../../../components/FormComponents/NumericInput";
import { ITheme } from "../../../utils/theme";
import { IEmployeeInfo, IPaidPersonalLeaveRequestData } from "../PaidPersonalLeaveRequestData";
import { defaultDisclaimer, IDisclaimer, MAX_ATTACHMENTS } from "../PaidPersonalLeaveRequestModels";
import { Disclaimer } from "./Disclaimer";

interface IDaysRequestedProps {
  data: IPaidPersonalLeaveRequestData;
  disclaimer: IDisclaimer;
  onShowDisclaimer: (disclaimer: IDisclaimer) => void;
  error: Record<string, JSX.Element | string>;
  onDateChange: (field: keyof IEmployeeInfo, value: string) => void;
  onDateBlur: (field: keyof IEmployeeInfo) => void;
  onFileChange: (files: FileList | []) => void;
  onRemoveFile: (index: number) => void;
  blurredFields: Partial<Record<keyof IEmployeeInfo, boolean>>
}

export const DaysRequested: React.FC<IDaysRequestedProps> = ({
  data,
  disclaimer,
  onShowDisclaimer,
  error,
  onDateChange,
  onDateBlur,
  onFileChange,
  onRemoveFile,
  blurredFields
}) => (
  <Box sx={{ p: { xs: 0, md: 2 } }}>
    {disclaimer && (!!disclaimer?.title || !!disclaimer.text?.length) && (
      <Disclaimer
        onClick={() => onShowDisclaimer(defaultDisclaimer)}
        title={disclaimer?.title || ""}
        text={disclaimer?.text || []}
        bulletParagraph={disclaimer.bulletParagraph}
      />
    )}
    <Grid container spacing={2}>
      <Grid item xs={6} sx={{ p: 0 }}>
        <Box
          sx={{ display: "flex", cursor: "pointer" }}
          onClick={() =>
            onShowDisclaimer({
              title: "Situations Covered",
              text: [
                "Responsibilities regarding health or care of a family member",
                "Responsibilities regarding education of a family member who is less than 18 years of age",
                "Urgent matters concerning employee or family member",
                "Attending citizenship ceremony",
                "Any other reason prescribed by regulation"
              ],
              bulletParagraph: true
            })
          }
        >
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
          <Typography sx={{ color: "corporateBlue", textDecoration: 'underline' }}>Situation Covered</Typography>
        </Box>
      </Grid>
      <Grid item xs={6} sx={{ p: 0 }}>
        <Box
          sx={{ display: "flex", cursor: "pointer" }}
          onClick={() =>
            onShowDisclaimer({
              title: "Additional Information",
              text: [
                'May be taken in one or more periods',
                'Periods must be at least one (1) day',
                'Applications not submitted within 1 week following the return to work of the leave may be denied',
                'The Association may review shift history (i.e. preference) to make a determination of whether the worker would have likely missed work on a particular shift.',
              ],
              bulletParagraph: true
            })
          }
        >
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
          <Typography sx={{ color: "corporateBlue", textDecoration: 'underline' }}>Additional Information</Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="form-label">
          Requested Period Of Leave
          <Typography component={"span"} sx={{ color: (theme: ITheme) => theme.palette.red.A700 }}>
            &nbsp;*
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DateField
          label="From"
          value={data?.employeeInfo.requestedDateFrom || ""}
          onChange={(value) => onDateChange("requestedDateFrom", value)}
          onBlur={() => onDateBlur("requestedDateFrom")}
          required
          error={error?.requestedDateFrom || error.requestedDateTo}
        />
      </Grid>
      <Grid item xs={12}>
        <DateField
          label="To"
          value={data?.employeeInfo.requestedDateTo || ""}
          onChange={(value) => onDateChange("requestedDateTo", value)}
          onBlur={() => onDateBlur("requestedDateTo")}
          required
          error={error?.requestedDateTo || error.requestedDateFrom}
        />
      </Grid>
      {
        (error?.requestedDateTo || error.requestedDateFrom) && <Grid item xs={12}>
          <FormHelperText
            component={'div'}
            error={!!error}
          >
            {error?.requestedDateTo || error.requestedDateFrom}
          </FormHelperText>
        </Grid>
      }
      <Grid item xs={12}>
        <NumericInput
          label="Number of Days"
          value={data?.employeeInfo.numberOfDaysRequested ?? null}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex' }}>
          <HelpOutlineIcon sx={{ color: "corporateBlue", mr: 1 }} />
          <Box
            sx={{ display: "flex", cursor: "pointer" }}
            onClick={() =>
              onShowDisclaimer({
                title: "Information Required",
                text: [
                  'Please attach applicable documentation to this application within 15 days of return to work if reasonably practicable to provide.',
                  'If outside of Vancouver (Local 500), please have your union representative confirm that you would have received work on the day and shift for which Personal Leave is sought.',
                ],
                bulletParagraph: true
              })
            }
          >
            <Typography sx={{ color: "corporateBlue", textDecoration: 'underline' }}>Information Required</Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ color: (theme: ITheme) => theme.palette.corporateBlue }}>Notes:</Typography>
          <Typography sx={{ color: (theme: ITheme) => theme.palette.corporateBlue }}>1. Each file size must not exceed 1 MB.</Typography>
        </Box>
        <FileUploadField
          onRemoveFile={onRemoveFile}
          onFileChange={(files) => onFileChange(files)}
          files={data?.employeeInfo.attachments || []}
          disabled={data?.employeeInfo?.attachments.length >= MAX_ATTACHMENTS}
          error={blurredFields.attachments ? error?.attachments : ''}
        />
      </Grid>
    </Grid>
  </Box>
);
